import {
  SliderModal,
  Button,
  Alert,
  AlertDescription,
  AlertTitle,
  Field,
  Input,
  Checkbox
} from '@see-ui/core';
import { CirclePlusIcon } from 'lucide-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import * as service from '@services/api/member-pharmacy-service';
import { useGlobalStore } from '@services/store/global-store';

interface Props {
  onClose: () => void;
  flowType: string; // 'dashboard' or 'cart'
  onSelectAddress?: (address: any) => void;
  onAddressUpdated?: () => void;
}

const UpdateDeliveryAddressSlider: React.FC<Props> = ({
  onClose,
  flowType,
  onSelectAddress,
  onAddressUpdated
}) => {
  const [editSliderOpen, setEditSliderOpen] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [primaryAddress, setPrimaryAddress] = useState<any>(null);
  const { memberDetails } = useGlobalStore((state) => state);
  const [suggestedAddress, setSuggestedAddress] = useState<any>(null);
  const [useSuggested, setUseSuggested] = useState(false);
  const [deliveryAddresses, setDeliveryAddresses] = useState<any[]>([]);

  useEffect(() => {
    fetchDeliveryAddress();
  }, []);

  const fetchDeliveryAddress = async () => {
    try {
      const req = {
        hcid: memberDetails?.member?.memberPolicy?.hcid
      };
      const response = await service.pharmacyAddresses(req);
      if (response?.data && response.data.addresses?.length > 0) {
        setDeliveryAddresses(response.data.addresses);
        const primary = response.data.addresses.find(
          (address) => address.isPrimary === true
        );
        setPrimaryAddress(primary || null);
      }
      console.log('pharmacyAddresses data:', response);
    } catch (error) {
      console.error('Error fetching pharmacy data:', error);
    }
  };

  const handleEditClick = (address: any) => {
    setSelectedAddress({ ...address, type: 'edit' });
    setEditSliderOpen(true);
    setSuggestedAddress(null);
  };

  const closeEditSlider = () => {
    setEditSliderOpen(false);
    setSelectedAddress(null);
    setSuggestedAddress(null);
  };

  useEffect(() => {
    if (useSuggested) {
      handleSaveAddress();
    }
  }, [useSuggested]);

  const handleSaveAddress = async () => {
    try {
      let saveReq = {
        address: {
          addressLine1: selectedAddress.addressLine1,
          addressLine2: selectedAddress.addressLine2,
          cityName: selectedAddress.cityName,
          state: selectedAddress.state,
          zipCode: selectedAddress.zipCode,
          isDefault: selectedAddress.isPrimary,
          addressTypeCode: 'PDA',
          isBlacklisted: false,
          deliveryInstructionText: '',
          zipCodeSuffix: '',
          id: ''
        },
        hcid: memberDetails?.member?.memberPolicy?.hcid
      };

      if (!suggestedAddress) {
        let resp = await service.validateAddress(saveReq);
        if (resp?.data) {
          setSuggestedAddress(resp.data);
        }
      } else {
        if (useSuggested) {
          saveReq.address.addressLine1 = suggestedAddress.addressLine1;
          saveReq.address.cityName = suggestedAddress.cityName;
          saveReq.address.state = suggestedAddress.state;
          saveReq.address.zipCode =
            suggestedAddress.zipCode?.substring(0, 5) || '';
          saveReq.address.zipCodeSuffix =
            suggestedAddress.zipCode?.substring(5) || '';
        }
        saveReq.address.zipCodeSuffix =
          suggestedAddress.zipCode?.substring(5) || '';
        saveReq.address.state = suggestedAddress.state;
        let res;
        if (selectedAddress.type === 'edit') {
          saveReq.address.id = selectedAddress.id;
          res = await service.updateDeliveryAddress(saveReq);
        } else {
          res = await service.addDeliveryAddress(saveReq);
        }

        if (res?.data?.message === 'SUCCESS') {
          fetchDeliveryAddress();
          if (onAddressUpdated) {
            onAddressUpdated();
          }
          onClose();
        }
      }
    } catch (error) {
      console.error('Failed to save address:', error);
    }
  };

  const handleSetAsPrimary = async (address: any) => {
    try {
      const payload = {
        ...address,
        isPrimary: true,
        isDefault: true
      };

      let saveReq = {
        address: payload,
        hcid: memberDetails?.member?.memberPolicy?.hcid
      };
      await service.updateDeliveryAddress(saveReq);
      fetchDeliveryAddress();
      if (onAddressUpdated) {
        onAddressUpdated();
      }
      onClose();
    } catch (error) {
      console.error('Failed to set as primary:', error);
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    try {
      const res = await service.deleteDeliveryAddress({
        id: addressId,
        hcid: memberDetails?.member?.memberPolicy?.hcid
      });
      fetchDeliveryAddress();
      if (onAddressUpdated) {
        onAddressUpdated();
      }
      onClose();
    } catch (error) {}
  };
  return (
    <>
      <SliderModal
        direction="right"
        labelAriaCloseBtn="Close Modal"
        onClose={onClose}
        open
      >
        <SliderModal.Body>
          <div className="px-10">
            <h2 className="pb-5">Change Delivery Address</h2>
            <span>
              Set your primary address to use it for all orders and
              communications.
            </span>

            <div className="py-5">
              <Alert variant="info">
                <React.Fragment key=".0">
                  <AlertDescription className="text-sm">
                    To delete your existing primary address, and add a new
                    address or select an existing address and then set it as
                    your primary address.
                  </AlertDescription>
                </React.Fragment>
              </Alert>
            </div>
            <div className="mt-5 flex flex-wrap gap-9">
              {deliveryAddresses.map((address, index) => (
                <div
                  key={index}
                  className="flex flex-col items-start border p-4 rounded shadow-sm w-1/4" // Adjusted width to 1/4 for smaller boxes
                  style={{ width: '250px', height: '300px' }} // Added maxWidth and height for precise control
                >
                  <p>{address.addressLine1}</p>
                  <div className="flex space-x-2">
                    <p>{address.cityName}</p>
                    <p>{address.state}</p>
                    <p>{address.zipCode}</p>
                    {'-'}
                    {address?.zipCodeSuffix}
                  </div>
                  <p className="pt-5 font-semibold">
                    {address.isPrimary ? 'Primary' : ''}
                  </p>
                  {address?.status === 'EXPIRES' && (
                    <>
                      <p className="font-semibold">
                        Inactive Temporary Address
                      </p>
                      <p>
                        {' '}
                        {moment(address.effectiveDate).format(
                          'MM/DD/YYYY'
                        )}{' '}
                        {' - '}{' '}
                        {moment(address.terminationDate).format('MM/DD/YYYY')}
                      </p>
                    </>
                  )}

                  <div className="pt-10"></div>
                  <div className="flex-grow"></div>
                  {!address.isPrimary && address?.status !== 'EXPIRES' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full  mt-2"
                      rounded
                      onClick={() => handleSetAsPrimary(address)}
                    >
                      Set as primary
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full mt-2"
                    rounded
                    onClick={() => handleEditClick(address)} // Open slider on Edit click
                  >
                    Edit
                  </Button>

                  {!address.isPrimary && (
                    <Button
                      variant="link"
                      size="sm"
                      className="w-full mt-auto underline"
                      rounded
                      onClick={() => handleDeleteAddress(address.id)}
                    >
                      Delete
                    </Button>
                  )}
                  {flowType === 'cart' && (
                    <Button
                      variant="link"
                      size="sm"
                      className="w-full mt-auto underline"
                      rounded
                      onClick={() => {
                        if (onSelectAddress) onSelectAddress(address);
                        onClose();
                      }}
                    >
                      Use this address
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <Button
            className="ml-5"
            variant="link"
            onClick={() => {
              setSelectedAddress({
                type: 'add',
                addressLine1: '',
                addressLine2: '',
                cityName: '',
                state: '',
                zipCode: '',
                zipCodeSuffix: '',
                isPrimary: false,
                status: ''
              });
              setEditSliderOpen(true);
            }}
          >
            <CirclePlusIcon className="text-gray-500" />
            Add new address
          </Button>
        </SliderModal.Body>
        <SliderModal.Footer>
          <Button onClick={onClose}>Close</Button>
        </SliderModal.Footer>
      </SliderModal>

      {editSliderOpen && (
        <SliderModal
          direction="right"
          labelAriaCloseBtn="Close Edit Modal"
          onClose={closeEditSlider}
          open
        >
          <SliderModal.Body>
            <div className="px-10">
              <h2 className="pb-5">
                {selectedAddress.type === 'add'
                  ? 'Add Delivery Address for Prescriptions'
                  : 'Edit Delivery Address for Prescriptions'}
              </h2>
              <p>Check the box to set this as your primary address.</p>
              <div className="bg-gray-100 rounded p-4 mt-4">
                <h4 className="pb-2">My current primary delivery address</h4>
                <p>{primaryAddress?.addressLine1}</p>
                <p>
                  {primaryAddress?.cityName} {primaryAddress?.state}{' '}
                  {primaryAddress?.zipCode}
                  {primaryAddress?.zipCodeSuffix
                    ? `-${primaryAddress?.zipCodeSuffix}`
                    : ''}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 pt-5">
                <div>
                  <Field
                    label={
                      <span className="font-semibold">Address line 1</span>
                    }
                    name="title"
                  >
                    <Input
                      value={selectedAddress.addressLine1}
                      onChange={(e) =>
                        setSelectedAddress({
                          ...selectedAddress,
                          addressLine1: e.target.value
                        })
                      }
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-1 gap-4 pt-5">
                  <div>
                    <Field
                      label={
                        <span className="font-semibold">
                          Address line 2 (Optional)
                        </span>
                      }
                      name="title"
                    >
                      <Input
                        value={selectedAddress.addressLine2}
                        onChange={(e) =>
                          setSelectedAddress({
                            ...selectedAddress,
                            addressLine2: e.target.value
                          })
                        }
                      />
                    </Field>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-3 gap-3 ">
                <div>
                  <Field
                    label={<span className="font-semibold">ZIP Code</span>}
                    name="title"
                  >
                    <Input
                      maxLength={5}
                      value={selectedAddress.zipCode || ''}
                      onChange={(e) =>
                        setSelectedAddress({
                          ...selectedAddress,
                          zipCode: e.target.value.replace(/\D/g, '') // Only digits
                        })
                      }
                    />
                  </Field>
                </div>
                <div>
                  <Field
                    label={<span className="font-semibold">City</span>}
                    name="title"
                  >
                    <Input
                      value={selectedAddress.cityName}
                      onChange={(e) =>
                        setSelectedAddress({
                          ...selectedAddress,
                          cityName: e.target.value
                        })
                      }
                    />
                  </Field>
                </div>
                <div>
                  <Field
                    label={<span className="font-semibold">State</span>}
                    name="title"
                  >
                    <Input
                      value={selectedAddress.state}
                      onChange={(e) =>
                        setSelectedAddress({
                          ...selectedAddress,
                          state: e.target.value
                        })
                      }
                    />
                  </Field>
                </div>
              </div>
              {selectedAddress?.status !== 'EXPIRES' && (
                <div className="pt-5">
                  <Checkbox
                    label="Check the box to set this as your primary address."
                    className="pt-4"
                    name="primary"
                    required
                    checked={selectedAddress?.isPrimary || false}
                    onCheckedChange={(checekd) =>
                      setSelectedAddress({
                        ...selectedAddress,
                        isPrimary: checekd
                      })
                    }
                  />
                </div>
              )}
            </div>

            {suggestedAddress && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6 rounded">
                <div className="font-semibold mb-2">Suggested Address</div>
                <div>
                  <div>{suggestedAddress.addressLine1}</div>
                  <div>
                    {suggestedAddress.cityName}, {suggestedAddress.state}{' '}
                    {suggestedAddress.zipCode}
                  </div>
                  {/* <div>Type: {suggestedAddress.addressType}</div>
                  <div>
                    {suggestedAddress.message && (
                      <span className="text-yellow-700">
                        {suggestedAddress.message}
                      </span>
                    )}
                  </div> */}
                </div>
                <Button
                  className="mt-3"
                  variant="primary"
                  onClick={() => {
                    setUseSuggested(true);
                  }}
                >
                  Use Suggested Address
                </Button>
              </div>
            )}
          </SliderModal.Body>
          <SliderModal.Footer>
            <Button onClick={closeEditSlider} variant="secondary">
              Close
            </Button>
            <Button
              variant="primary"
              className="ml-4"
              onClick={handleSaveAddress}
            >
              Save
            </Button>
          </SliderModal.Footer>
        </SliderModal>
      )}
    </>
  );
};

export default UpdateDeliveryAddressSlider;
